import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Breadcrumbs, Card, Chip } from '../../components';
import useDocumentHead from '../../hooks/useDocumentHead';
import { useLang, type Lang } from '../../i18n';
import { get } from '../../services/api';
import './BlogPostPage.scss';

interface Block {
  type: 'heading' | 'paragraph' | 'list' | 'quote' | 'text' | 'image';
  text?: string;
  items?: string[];
  author?: string;
  content?: string;
  image?: string;
  alt?: string;
}
interface RelatedPost { slug: string; title: string }
interface Post {
  slug: string;
  title: string;
  excerpt?: string;
  cover?: string;
  cover_image?: string;
  body_blocks?: Block[];
  body?: Block[];
  author_name?: string;
  author_avatar?: string;
  published_at?: string;
  reading_time?: number;
  tags?: string[];
  related?: RelatedPost[];
}

const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

function renderBlock(b: Block, i: number) {
  switch (b.type) {
    case 'heading':
      return <h2 key={i} className="blog-post__h2">{b.text}</h2>;
    case 'paragraph':
      return <p key={i} className="blog-post__p">{b.text}</p>;
    case 'list':
      return (
        <ul key={i} className="blog-post__list">
          {(b.items || []).map((item, j) => <li key={j}>{item}</li>)}
        </ul>
      );
    case 'quote':
      return (
        <blockquote key={i} className="blog-post__quote">
          <p>«{b.text}»</p>
          {b.author && <cite>— {b.author}</cite>}
        </blockquote>
      );
    case 'text':
      if (b.content) return <div key={i} className="blog-post__prose" dangerouslySetInnerHTML={{ __html: b.content }} />;
      return null;
    case 'image':
      if (b.image) return <img key={i} src={b.image} alt={b.alt || ''} className="blog-post__image" />;
      return null;
    default:
      return null;
  }
}

export default function BlogPostPage() {
  const { t, lang } = useLang();
  const { slug = '' } = useParams();
  const [data, setData] = useState<Post | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await get<Post>(`/blog/${slug}/?lang=${LANG_MAP[lang]}`);
        if (!cancel) setData(res);
      } catch {
        if (!cancel) setData(null);
      }
    })();
    return () => { cancel = true; };
  }, [slug, lang]);

  const title = data?.title || slug;
  const cover = data?.cover || data?.cover_image;
  const blocks = data?.body_blocks || data?.body || [];
  const publishedNice = data?.published_at
    ? new Date(data.published_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  useDocumentHead({
    title: `${title} — ${t('blog.title')} — ${t('brand.name')}`,
    description: data?.excerpt || t('blog.sub'),
    canonical: `https://ukam.uz/blog/${slug}`,
    og: { title, description: data?.excerpt, image: cover, type: 'article' },
    schemaOrg: [
      { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
        { '@type': 'ListItem', position: 1, name: t('brand.name'), item: 'https://ukam.uz/' },
        { '@type': 'ListItem', position: 2, name: t('blog.title'), item: 'https://ukam.uz/blog' },
        { '@type': 'ListItem', position: 3, name: title, item: `https://ukam.uz/blog/${slug}` },
      ] },
      { '@context': 'https://schema.org', '@type': 'Article',
        headline: title, image: cover, datePublished: data?.published_at,
        author: data?.author_name ? { '@type': 'Person', name: data.author_name } : undefined,
        publisher: { '@type': 'Organization', name: 'Ukam IT' },
      },
    ],
  });

  return (
    <div className="blog-post">
      <article>
        <header className="blog-post__hero">
          <div className="container">
            <Breadcrumbs items={[
              { label: t('brand.name'), href: '/' },
              { label: t('blog.title'), href: '/blog' },
              { label: title },
            ]} />
            {data?.excerpt && <p className="blog-post__excerpt">{data.excerpt}</p>}
            <h1 className="blog-post__title">{title}</h1>
            <div className="blog-post__meta">
              {data?.author_name && (
                <span className="blog-post__author">
                  {data.author_avatar && <img src={data.author_avatar} alt="" />}
                  <span>{data.author_name}</span>
                </span>
              )}
              {publishedNice && <span>{publishedNice}</span>}
              {data?.reading_time ? <span>{t('blog.reading_time', { minutes: data.reading_time })}</span> : null}
            </div>
            {cover && (
              <div className="blog-post__cover"><img src={cover} alt="" /></div>
            )}
          </div>
        </header>
        <div className="container blog-post__content">
          <div className="blog-post__body">
            {blocks.length === 0 ? (
              <p className="blog-post__empty">{data === null ? t('blog.sub') : ''}</p>
            ) : blocks.map(renderBlock)}
          </div>
          {data?.tags && data.tags.length > 0 && (
            <div className="blog-post__tags">
              <Badge variant="neutral">{t('blog.tags')}</Badge>
              {data.tags.map((tg) => (
                <Link key={tg} to={`/blog?tag=${encodeURIComponent(tg)}`} className="blog-post__tag-link">
                  <Chip variant="neutral">{tg}</Chip>
                </Link>
              ))}
            </div>
          )}
          {data?.related && data.related.length > 0 && (
            <section className="blog-post__related">
              <h2 className="blog-post__h2">{t('blog.related')}</h2>
              <div className="grid grid--3">
                {data.related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="blog-post__rel-link">
                    <Card variant="bordered" padding="md">
                      <h3 className="blog-post__rel-title">{r.title}</h3>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </div>
  );
}
